import { API_ENDPOINTS } from '../api-endpoints';
import type { InternalErrorHandler } from '../error-handling/types';
import { fetchJSON } from '../utils/fetch-json';
import { endpointToHash } from './endpoint-hash';

const REQUEST_NOTIFICATION_PERMISSION_AFTER_LOGIN_KEY =
  'mayer:request-notification-permission-after-login';

export enum NotificationSubscriptionResult {
  OK = 'OK',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  ERROR = 'ERROR',
}

interface VapidPublicKeyResponse {
  publicKey: string;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  return Notification.requestPermission();
}

/**
 * Helper functions to manage the notification permission prompt after login
 */

export function scheduleNotificationPermissionPromptAfterLogin(): void {
  sessionStorage.setItem(REQUEST_NOTIFICATION_PERMISSION_AFTER_LOGIN_KEY, '1');
}

export function handleNotificationPermissionPromptAfterLogin(): void {
  const shouldPrompt =
    sessionStorage.getItem(REQUEST_NOTIFICATION_PERMISSION_AFTER_LOGIN_KEY) === '1';

  if (!shouldPrompt || Notification.permission !== 'default') {
    sessionStorage.removeItem(REQUEST_NOTIFICATION_PERMISSION_AFTER_LOGIN_KEY);

    return;
  }

  sessionStorage.removeItem(REQUEST_NOTIFICATION_PERMISSION_AFTER_LOGIN_KEY);

  requestNotificationPermission().catch((error: unknown) => {
    console.error('Error requesting notification permission after login:', error);
  });
}

/**
 * Helper functions to manage push notification subscriptions
 */

async function getServiceWorkerSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

async function getVapidPublicKey(): Promise<string> {
  const response = await fetchJSON<VapidPublicKeyResponse>(
    API_ENDPOINTS.pushNotifications.publicKey,
    {
      method: 'GET',
    },
  );
  return response.publicKey;
}

async function createPushSubscription(publicKey: string): Promise<PushSubscription> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: publicKey,
  });
}

export async function subscribeToNotifications(
  errorHandler: InternalErrorHandler,
): Promise<NotificationSubscriptionResult> {
  try {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission denied');

      return NotificationSubscriptionResult.PERMISSION_DENIED;
    }

    const publicKey = await getVapidPublicKey();
    const subscription = await createPushSubscription(publicKey);

    await fetchJSON<void>(API_ENDPOINTS.pushNotifications.subscriptions, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscription),
    });

    return NotificationSubscriptionResult.OK;
  } catch (error: unknown) {
    console.error('Error subscribing to notifications:', error);
    errorHandler({ error, caption: 'error.generic-message' });

    return NotificationSubscriptionResult.ERROR;
  }
}

/**
 * Helper function to unsubscribe from push notifications
 */
export async function unsubscribeFromNotifications(
  errorHandler: InternalErrorHandler,
): Promise<NotificationSubscriptionResult> {
  try {
    const subscription = await getServiceWorkerSubscription();
    if (!subscription) {
      return NotificationSubscriptionResult.OK;
    }

    const { endpoint: subscriptionEndpoint } = subscription;
    const hash = await endpointToHash(subscriptionEndpoint);
    await fetchJSON<void>(`${API_ENDPOINTS.pushNotifications.subscriptions}/${hash}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await subscription.unsubscribe();

    return NotificationSubscriptionResult.OK;
  } catch (error: unknown) {
    errorHandler({ error, caption: 'error.generic-message' });

    return NotificationSubscriptionResult.ERROR;
  }
}
